import React, { useEffect, useMemo, useState } from 'react';
import {
  Avatar,
  Badge,
  Button,
  Drawer,
  Empty,
  List,
  Space,
  Spin,
  Switch,
  Tag,
  Typography,
  message,
} from 'antd';
import {
  BellOutlined,
  CheckOutlined,
  ReloadOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import { history } from '@umijs/max';
import notificationService, {
  NotificationPreference,
  NotificationRecipientItem,
} from '@/services/NotificationService';
import { fetchUsers } from '@/services/ProjectService';
import userConfigService from '@/services/UserConfigService';
import { buildProjectPanelUrl } from '@/utils/projectPanelRoute';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import 'dayjs/locale/es';

dayjs.extend(relativeTime);
dayjs.locale('es');

const { Text } = Typography;

function formatCategory(category: string): { label: string; color: string } {
  switch (category) {
    case 'project':
      return { label: 'Proyectos', color: 'blue' };
    case 'comment':
      return { label: 'Comentarios', color: 'purple' };
    case 'lead':
      return { label: 'Leads', color: 'gold' };
    case 'system':
      return { label: 'Sistema', color: 'default' };
    default:
      return { label: category, color: 'default' };
  }
}

function safeText(v: any): string {
  if (v === null || v === undefined) return '';
  return String(v);
}

function getNotificationMeta(n: { meta?: any } | null): any {
  if (!n) return {};
  const raw = n.meta;
  if (!raw) return {};
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw);
    } catch {
      return {};
    }
  }
  return raw;
}

function getProjectIdFromNotification(n: { meta?: any; entity_id?: number | null } | null): number | null {
  if (!n) return null;
  const meta = getNotificationMeta(n);
  const candidate = meta.fk_project || meta.project_id || meta.projectId || meta.project?.id || n.entity_id;
  const parsed = Number(candidate);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

function getClientIdFromNotification(n: { meta?: any } | null): number | null {
  if (!n) return null;
  const meta = getNotificationMeta(n);
  const candidate =
    meta.fk_client || meta.client_id || meta.clientId || meta.client?.id || meta.project?.fk_client;
  const parsed = Number(candidate);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

function getProjectNameFromNotification(n: { meta?: any; title?: string | null; body?: string | null } | null): string | null {
  if (!n) return null;
  const meta = getNotificationMeta(n);
  const candidate = meta.project_name || meta.projectName || meta.project?.name || null;
  if (candidate) return String(candidate);
  const title = typeof n.title === 'string' ? n.title : '';
  const body = typeof n.body === 'string' ? n.body : '';
  const regex = /nuevo comentario en(?: el proyecto)?[:\s]+(.+)/i;
  const fromTitle = title.match(regex)?.[1];
  if (fromTitle) return fromTitle.trim();
  const fromBody = body.match(regex)?.[1];
  if (fromBody) return fromBody.trim();
  return null;
}

function routeForNotification(item: NotificationRecipientItem): string | null {
  const n = item.notification;
  if (!n) return null;
  const clientId = getClientIdFromNotification(n);
  const projectId = getProjectIdFromNotification(n);

  if (n.category === 'lead') {
    return '/PotentialClients';
  }
  if (n.category === 'project' || n.category === 'comment') {
    if (clientId) {
      return buildProjectPanelUrl({
        view: 'clients',
        clientId,
        projectId,
      });
    }
    return projectId ? `/Project?projectId=${encodeURIComponent(String(projectId))}` : '/Project';
  }
  return null;
}

const NotificationsBell: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<'notifications' | 'preferences'>('notifications');

  const [loading, setLoading] = useState(false);
  const [countLoading, setCountLoading] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  const [items, setItems] = useState<NotificationRecipientItem[]>([]);
  const [actorMap, setActorMap] = useState<Record<number, { name: string; photoUrl?: string }>>({});

  const [prefsLoading, setPrefsLoading] = useState(false);
  const [preferences, setPreferences] = useState<NotificationPreference[]>([]);

  const isMobile = typeof window !== 'undefined' ? window.innerWidth < 768 : false;

  const filteredItems = useMemo(() => (items || []).filter((it) => !!it.notification), [items]);

  const refreshUnreadCount = async () => {
    setCountLoading(true);
    try {
      const count = await notificationService.getUnreadCount();
      setUnreadCount(count);
    } finally {
      setCountLoading(false);
    }
  };

  const refreshPreferences = async () => {
    setPrefsLoading(true);
    try {
      const prefs = await notificationService.getPreferences();
      setPreferences(prefs || []);
    } finally {
      setPrefsLoading(false);
    }
  };

  async function hydrateActors(data: NotificationRecipientItem[]) {
    try {
      const actorIds = Array.from(new Set(
        (data || [])
          .map((it) => Number(it.notification?.fk_actor_user))
          .filter((id) => Number.isFinite(id) && id > 0),
      ));
      if (!actorIds.length) return;

      const users = await fetchUsers().catch(() => []);
      const userMap: Record<number, { name?: string }> = {};
      (users || []).forEach((u: any) => {
        if (u && u.id !== null && u.id !== undefined) userMap[Number(u.id)] = { name: u.name };
      });

      const entries = await Promise.all(actorIds.map(async (id) => {
        const existing = actorMap[id];
        if (existing?.name && existing?.photoUrl) return [id, existing] as const;
        let photoUrl: string | undefined;
        try {
          const dataUrl = await userConfigService.getUserPhotoDataUrl(id);
          if (dataUrl) photoUrl = dataUrl;
        } catch (e) {}
        return [id, { name: userMap[id]?.name || `Usuario ${id}`, photoUrl }] as const;
      }));

      setActorMap((prev) => {
        const next = { ...prev };
        entries.forEach(([id, info]) => {
          next[id] = { ...next[id], ...info };
        });
        return next;
      });
    } catch (e) {
      // silent
    }
  }

  const refreshNotifications = async () => {
    setLoading(true);
    try {
      const data = await notificationService.fetchNotifications({
        limit: 50,
        offset: 0,
      });
      setItems(data || []);
      await hydrateActors(data || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshUnreadCount();
    const timer = window.setInterval(() => {
      refreshUnreadCount();
    }, 30000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!open) return;
    if (tab === 'notifications') {
      refreshNotifications();
      refreshUnreadCount();
    }
    if (tab === 'preferences') {
      refreshPreferences();
    }
  }, [open, tab]);

  const formatDate = (value?: string | null) => {
    if (!value) return '';
    const parsed = dayjs(value);
    if (!parsed.isValid()) return safeText(value);
    return `${parsed.format('DD MMM YYYY, HH:mm')} · ${parsed.fromNow()}`;
  };

  const markAllRead = async () => {
    const ok = await notificationService.markAllRead();
    if (!ok) {
      message.error('No se pudo marcar todo como leído');
      return;
    }
    message.success('Notificaciones marcadas como leídas');
    await refreshNotifications();
    await refreshUnreadCount();
  };

  const markOneRead = async (recipientId: number) => {
    const ok = await notificationService.markRead(recipientId);
    if (!ok) {
      message.error('No se pudo marcar como leído');
      return;
    }
    setItems((prev) =>
      (prev || []).map((x) => (x.id === recipientId ? { ...x, read_at: x.read_at || new Date().toISOString() } : x)),
    );
    await refreshUnreadCount();
  };

  const togglePref = async (cat: string, key: 'in_app' | 'email', value: boolean) => {
    const next = (preferences || []).map((p) =>
      p.category === cat ? { ...p, [key]: value } : p,
    );
    setPreferences(next);

    setPrefsLoading(true);
    try {
      const saved = await notificationService.updatePreferences(next);
      if (!saved || saved.length === 0) {
        message.warning('No se pudieron guardar las preferencias');
      } else {
        message.success('Preferencias guardadas');
      }
    } finally {
      setPrefsLoading(false);
    }
  };

  const renderNotifications = () => {
    if (loading) {
      return (
        <div style={{ padding: 16, textAlign: 'center' }}>
          <Spin />
        </div>
      );
    }

    if (!filteredItems.length) {
      return (
        <div style={{ padding: 16 }}>
          <Empty description="Sin notificaciones" />
        </div>
      );
    }

    return (
      <List
        itemLayout="vertical"
        dataSource={filteredItems}
        renderItem={(it) => {
          const n = it.notification;
          if (!n) return null;

          const cat = formatCategory(String(n.category || 'system'));
          const isUnread = !it.read_at;
          const actorId = Number(n.fk_actor_user || 0);
          const actor = actorId ? actorMap[actorId] : undefined;
          const actorName = actor?.name || 'Sistema';
          const avatarFallback = actorName ? actorName.charAt(0).toUpperCase() : 'S';
          const projectId = getProjectIdFromNotification(n);
          const clientId = getClientIdFromNotification(n);
          const projectName = getProjectNameFromNotification(n);
          const notificationTitle =
            n.category === 'comment'
              ? (projectName ? `Nuevo comentario en ${projectName}` : safeText(n.title) || 'Nuevo comentario')
              : safeText(n.title) || 'Notificación';

          return (
            <List.Item
              key={it.id}
              style={{ cursor: 'pointer', padding: '16px 12px' }}
              onClick={async () => {
                const route = routeForNotification(it);
                if (projectId && !clientId && typeof window !== 'undefined') {
                  try {
                    localStorage.setItem('pendingProjectId', String(projectId));
                  } catch {}
                  try {
                    window.dispatchEvent(new CustomEvent('openProjectFromNotification', { detail: { projectId } }));
                  } catch {}
                }
                if (isUnread) {
                  await markOneRead(it.id);
                }
                setOpen(false);
                if (route) {
                  history.push(route);
                }
              }}
              actions={[
                <Button
                  key="markRead"
                  size="small"
                  type={isUnread ? 'primary' : 'default'}
                  icon={<CheckOutlined />}
                  onClick={async (e) => {
                    e.stopPropagation();
                    if (isUnread) await markOneRead(it.id);
                  }}
                  disabled={!isUnread}
                >
                  Leído
                </Button>,
              ]}
            >
              <div style={{ display: 'flex', gap: 12 }}>
                <Avatar src={actor?.photoUrl} size={40} style={{ background: '#e2e8f0', color: '#0f172a' }}>
                  {avatarFallback}
                </Avatar>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <Text style={{ fontWeight: 600, color: '#0f172a' }}>{actorName}</Text>
                      <Tag color={cat.color}>{cat.label}</Tag>
                    </div>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {formatDate(n.createdAt || '')}
                    </Text>
                  </div>

                  <div style={{ marginTop: 6 }}>
                    <Text strong style={{ fontSize: 14 }}>{notificationTitle}</Text>
                  </div>
                  <div style={{ marginTop: 4 }}>
                    <Text type="secondary">{safeText(n.body)}</Text>
                  </div>
                </div>
              </div>
            </List.Item>
          );
        }}
      />
    );
  };

  const renderPreferences = () => {
    if (prefsLoading) {
      return (
        <div style={{ padding: 16, textAlign: 'center' }}>
          <Spin />
        </div>
      );
    }

    const normalized: Record<string, NotificationPreference> = {};
    for (const cat of ['project', 'comment', 'lead', 'system']) {
      normalized[cat] = { category: cat, in_app: true, email: false };
    }
    for (const p of preferences || []) {
      if (p && p.category) normalized[String(p.category)] = p;
    }

    return (
      <div style={{ padding: 16 }}>
        <List
          dataSource={Object.values(normalized)}
          renderItem={(p) => {
            const cat = formatCategory(String(p.category));
            return (
              <List.Item
                key={String(p.category)}
                actions={[
                  <Space key="switches" size={16}>
                    <Space size={8}>
                      <Text>In-app</Text>
                      <Switch
                        checked={!!p.in_app}
                        onChange={(v) => togglePref(String(p.category), 'in_app', v)}
                      />
                    </Space>
                    <Space size={8}>
                      <Text>Email</Text>
                      <Switch
                        checked={!!p.email}
                        onChange={(v) => togglePref(String(p.category), 'email', v)}
                      />
                    </Space>
                  </Space>,
                ]}
              >
                <Space size={8}>
                  <Tag color={cat.color}>{cat.label}</Tag>
                  
                </Space>
              </List.Item>
            );
          }}
        />
      </div>
    );
  };

  return (
    <>
      <Badge count={unreadCount} size="small" overflowCount={99}>
        <Button
          type="text"
          aria-label="Notificaciones"
          icon={<BellOutlined />}
          onClick={() => setOpen(true)}
          style={{ padding: 4 }}
        />
      </Badge>

      <Drawer
        open={open}
        width={isMobile ? '100%' : 420}
        onClose={() => setOpen(false)}
        title={
          <Space size={12}>
            <Text strong>Notificaciones</Text>
            <Badge count={unreadCount} overflowCount={99} />
          </Space>
        }
        extra={
          <Space>
            <Button
              icon={<ReloadOutlined />}
              onClick={async () => {
                if (tab === 'notifications') {
                  await refreshNotifications();
                  await refreshUnreadCount();
                  return;
                }
                await refreshPreferences();
              }}
              loading={loading || countLoading || prefsLoading}
            />
            <Button
              icon={<SettingOutlined />}
              onClick={() => setTab((t) => (t === 'notifications' ? 'preferences' : 'notifications'))}
            />
          </Space>
        }
      >
        {tab === 'notifications' ? (
          <div>
            <Space direction="vertical" size={12} style={{ width: '100%' }}>
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  onClick={markAllRead}
                  icon={<CheckOutlined />}
                  disabled={unreadCount === 0}
                >
                  Marcar todo leído
                </Button>
              </div>
              {renderNotifications()}
            </Space>
          </div>
        ) : (
          renderPreferences()
        )}
      </Drawer>
    </>
  );
};

export default NotificationsBell;
